'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return;

    setLoading(true);
    try {
      await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      // Respuesta generica: no revela si el correo existe (anti-enumeracion).
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setLoading(false);
    }
  }, [email]);

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="absolute inset-0 -z-10 bg-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,#1a0b0a_0%,transparent_55%),radial-gradient(circle_at_90%_10%,#ff4500_8%,transparent_30%),radial-gradient(circle_at_20%_85%,#e31b23_10%,transparent_40%)]" />
      </div>

      <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          <Link
            href="/login"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a iniciar sesión
          </Link>

          <div className="text-center mb-8">
            <div className="inline-flex flex-col items-center gap-4 mb-4">
              <div className="relative">
                <img
                  src="/images/logo-live-moto.png"
                  alt="LIVE MOTO Logo"
                  className="w-40 h-auto object-contain drop-shadow-lg"
                />
              </div>
              <div className="text-center">
                <h1 className="text-3xl font-bold tracking-tight">
                  <span className="text-[#E31B23]">LIVE</span>{" "}
                  <span className="text-[#C0C0C0]">MOTO</span>
                </h1>
                <p className="text-[#FF4500] font-medium mt-1">Sistema de Gestión</p>
              </div>
            </div>
          </div>

          <Card
            className="bg-slate-900/70 border border-[#FF4500]/50 rounded-2xl p-6 sm:p-8 shadow-2xl backdrop-blur-md"
          >
            <CardContent className="pb-2">
              <p className="text-slate-300 text-center text-sm mb-5">
                Ingresa tu correo y te enviaremos instrucciones para restablecer tu contraseña
              </p>

              {sent ? (
                <div
                  role="status"
                  className="rounded-xl border border-emerald-900/60 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-300 flex items-start gap-3"
                >
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Si el correo está registrado, recibirás un mensaje con las instrucciones.
                  </span>
                </div>
              ) : null}

              {sent ? null : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-medium text-slate-300">
                      Correo electrónico
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 bg-slate-950/60 border-slate-700 text-slate-100 placeholder-slate-500 focus:border-[#FF4500] focus:ring-1 focus:ring-[#FF4500]"
                        placeholder="correo@ejemplo.com"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-gradient-to-r from-[#FF4500] to-[#E31B23] text-white font-semibold shadow-lg hover:from-[#FF6B35] hover:to-[#C0392B] focus-visible:ring-[#FF4500] focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                    disabled={loading}
                  >
                    {loading ? 'Enviando...' : 'Enviar instrucciones'}
                  </Button>

                  <div className="text-center text-xs text-slate-400">
                    ¿Recordaste tu contraseña?{' '}
                    <Link
                      href="/login"
                      className="text-[#FF4500] hover:text-[#FF6B35] font-medium"
                    >
                      Iniciar sesión
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Identidad protegida por Kawrium (disclaimer infraestructura) */}
          <div className="mt-3 text-center text-slate-500 text-xs">
            Identidad protegida por Kawrium
          </div>
          <div className="mt-1 text-center text-slate-500 text-xs">
            Este sistema utiliza tecnología de Kawrium
          </div>
        </div>
      </div>
    </div>
  );
}
